import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Badge, Input, InputProps, Subtitle2, Switch } from '@fluentui/react-components';
import { ArrowLeft24Regular } from '@fluentui/react-icons';
import { GuideDrawer, GuideDrawerSection } from '../../../theme/components/GuideDrawer';
import { GuideField, GuideFieldGrid } from '../../../theme/components/GuideField';

// component functions
import * as ComponentFunctions from './Consumption.Form.Functions';

// styles
import './Consumption.Form.Styles.css';

// sub-components
import { DateTimeInput } from '../../../commons/component/Inputs/DateTimeInput/DateTimeInput';
import ContractConceptSelectorComponent from '../../../commons/component/Inputs/ContractConceptSelector/ContractConceptSelector.Component';
import ContractStagesSelectorComponent from '../../../commons/component/Inputs/ContractStagesSelector/ContractStages.Selector.Component';
import { CurrencySelectorComponent } from '../../../commons/component/Inputs/CurrencySelector/CurrencySelectorComponent';
import ProtocolPatientSelectorComponent from '../../../commons/component/Inputs/ProtocolPatientSelector/ProtocolPatientSelector.Component';
import { ErrorMessageComponent } from '../../Error/ErrorMessageComponent';

// sub-components (continued)
import PractitionerSelectorComponent from '../../../commons/component/Inputs/PractitionerSelector/PractitionerSelectorComponent';

// models
import { ContractConcept } from '../../../models/Entities/Contracts/ContractConcept';
import { ContractStage } from '../../../models/Entities/Contracts/ContractStage';
import { ProtocolPatient } from '../../../models/Entities/Protocols/ProtocolPatient';
import { NewProtocolConsumption } from '../../../models/Entities/Protocols/NewProtocolConsumption';
import { ProtocolConsumptionConditionEnum } from '../../../models/Enums/ProtocolConsumptionConditionEnum';
import { Practitioner } from '../../../models/Entities/Practitioners/Practitioner';
import CurrencyAmountComponent from '../../../commons/component/CurrencyAmount/CurrencyAmount.Component';

// ── Local types ─────────────────────────────────────────────────────────────
interface BatchItemOverride {
    key: string;
    patientNumber: string;
    stageId: number;
    stageName: string;
    dueDate: Date | undefined;
    producer: Practitioner | undefined;
}

const ConsumptionFormComponent = (props: ComponentFunctions.Props) => {

    // ── form state ──────────────────────────────────────────────────────────
    const [submittedForm, setSubmittedForm] = useState(false);
    const [isAssociatedToContractConcept, setIsAssociatedToContractConcept] = useState(false);

    const [serviceName, setServiceName] = useState<string>('');
    const [serviceDueDate, setServiceDueDate] = useState<Date | undefined>();
    const [contractConcept, setContractConcept] = useState<ContractConcept | undefined>();
    const [selectedStages, setSelectedStages] = useState<ContractStage[]>([]);
    const [selectedPatients, setSelectedPatients] = useState<ProtocolPatient[]>([]);
    const [currencyCode, setCurrencyCode] = useState('ARS');
    const [quantity, setQuantity] = useState(1);
    const [unitAmount, setUnitAmount] = useState(0);

    // producer (paso 1 default)
    const [producer, setProducer] = useState<Practitioner | undefined>();

    // multi-step
    const [step, setStep] = useState<1 | 2>(1);
    const [batchOverrides, setBatchOverrides] = useState<BatchItemOverride[]>([]);

    // batch creation
    const creationQueueRef = useRef<NewProtocolConsumption[]>([]);
    const [batchTotal, setBatchTotal] = useState(0);
    const [batchCompleted, setBatchCompleted] = useState(0);

    // derived
    const amount = quantity * unitAmount;
    const effectivePatientsCount = selectedPatients.length > 0 ? selectedPatients.length : 1;
    const effectiveStagesCount = selectedStages.length > 0 ? selectedStages.length : 1;
    const isBatch = effectivePatientsCount * effectiveStagesCount > 1;

    // ── side effects ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (props.Protocol?.id) {
            props.GetMedicalProtocolById(props.Protocol.id);
        }
    }, [props.Protocol?.id]);

    useEffect(() => {
        if (props.Protocol?.id) {
            props.GetAllProtocolContractAssociations(props.Protocol.id);
        }
    }, [props.Protocol?.id]);

    useEffect(() => {
        if (props.successSaving) {
            const nextCompleted = batchCompleted + 1;
            setBatchCompleted(nextCompleted);
            if (creationQueueRef.current.length > 0) {
                const next = creationQueueRef.current.shift()!;
                props.CreateProtocolConsumption(next);
            } else {
                props.onClose();
                ResetForm();
            }
        }
    }, [props.successSaving]);

    useEffect(() => {
        if (props.failSaving) {
            creationQueueRef.current = [];
            setBatchTotal(0);
            setBatchCompleted(0);
        }
    }, [props.failSaving]);

    // ── handlers ─────────────────────────────────────────────────────────────
    const onServiceNameChange: InputProps['onChange'] = (_, data) => setServiceName(data.value);

    const onQuantityChange: InputProps['onChange'] = (_, data) => {
        const v = Number.parseFloat(data.value);
        setQuantity(Number.isNaN(v) ? 0 : v);
    };

    const onUnitAmountChange: InputProps['onChange'] = (_, data) => {
        const v = Number.parseFloat(data.value);
        setUnitAmount(Number.isNaN(v) ? 0 : v);
    };

    const onToggleContractConcept = (checked: boolean) => {
        setIsAssociatedToContractConcept(checked);
        if (!checked) setContractConcept(undefined);
    };

    const ValidateForm = (): boolean => {
        setSubmittedForm(true);
        return !!serviceName.trim();
    };

    const buildConsumption = (
        patientNumber: string,
        stageId: number,
        stageName: string,
        dueDate: Date | undefined,
        itemProducer: Practitioner | undefined
    ): NewProtocolConsumption => ({
        status: 0,
        protocolId: props.Protocol?.id ?? 0,
        serviceName: contractConcept?.concept?.conceptName ?? serviceName,
        serviceDueDate: dueDate ?? new Date(),
        contractConceptId: contractConcept?.id,
        conceptDescription: '',
        parentInstanceId: '',
        instanceId: '',
        patientNumberInProtocol: patientNumber,
        stageId: stageId,
        stageName: stageName,
        calculatingType: 0,
        currencyCode: currencyCode,
        quantity: quantity,
        coefficientApplied: 0,
        haveOverheadIncluded: false,
        contractValue: 0,
        overheadPercentage: 0,
        overheadAmount: 0,
        haveFeeDivision: false,
        feeDivisionPercentage: 0,
        ownerAmount: 0,
        ownerAmountWithholding: 0,
        ownerAmountWithholdingTotal: 0,
        teamDivisionPercentage: 0,
        teamAmount: 0,
        teamAmountWithholding: 0,
        teamAmountWithholdingTotal: 0,
        unitAmount: unitAmount,
        amount: amount,
        totalValues: 0,
        totalAmount: amount,
        totalAmountWithWithholding: amount,
        payeeAmount: 0,
        pendingAmount: amount,
        withHolingPercentage: 0,
        withHolingAmount: 0,
        costStatus: 0,
        costAmount: 0,
        costPayee: 0,
        costPending: 0,
        costCurrencyCode: '',
        typeOfService: 0,
        realizedType: 1,
        condition: ProtocolConsumptionConditionEnum.Normal,
        medicalServiceId: 0,
        medicalServiceName: '',
        medicalServiceCode: '',
        markedToSettle: false,
        markedAsPayee: false,
        markedAsFlagged: false,
        markedAsItemized: false,
        markedAsRefundable: false,
        markedAsNeedMoreInfo: false,
        moreInfoComments: '',
        markedAsHaveWarnings: false,
        warningsComments: '',
        comments: '',
        tag1: '', tag2: '', tag3: '', tag4: '',
        providerId: undefined,
        providerName: '',
        producer: itemProducer?.userId ?? undefined,
        producerName: itemProducer?.fullName ?? '',
        emrCrossVersionId: undefined,
        // Un consumo creado desde el formulario no es el costo de un
        // concepto facturado dentro de otro.
        isBilledConceptCost: false
    });

    const updateBatchOverride = (index: number, partial: Partial<Pick<BatchItemOverride, 'dueDate' | 'producer'>>) => {
        setBatchOverrides(prev =>
            prev.map((item, i) => (i === index ? { ...item, ...partial } : item))
        );
    };

    const onGoToStep2 = () => {
        if (!ValidateForm()) return;
        const patients = selectedPatients.length > 0
            ? selectedPatients.map(p => ({ number: p.numberInProtocol }))
            : [{ number: '' }];
        const stages = selectedStages.length > 0
            ? selectedStages.map(s => ({ id: s.id, name: s.stageName }))
            : [{ id: 0, name: '' }];
        const overrides: BatchItemOverride[] = [];
        for (const patient of patients) {
            for (const stage of stages) {
                overrides.push({
                    key: `${patient.number}||${stage.id}`,
                    patientNumber: patient.number,
                    stageId: stage.id,
                    stageName: stage.name,
                    dueDate: serviceDueDate,
                    producer: producer,
                });
            }
        }
        setBatchOverrides(overrides);
        setStep(2);
    };

    const onSubmitForm = () => {
        if (!ValidateForm()) return;

        let queue: NewProtocolConsumption[];

        if (isBatch) {
            // paso 2: usar overrides individuales
            queue = batchOverrides.map(ov =>
                buildConsumption(ov.patientNumber, ov.stageId, ov.stageName, ov.dueDate, ov.producer)
            );
        } else {
            // paso 1 directo (un solo concepto)
            const patients = selectedPatients.length > 0
                ? selectedPatients.map(p => ({ number: p.numberInProtocol }))
                : [{ number: '' }];
            const stages = selectedStages.length > 0
                ? selectedStages.map(s => ({ id: s.id, name: s.stageName }))
                : [{ id: 0, name: '' }];
            queue = [];
            for (const patient of patients) {
                for (const stage of stages) {
                    queue.push(buildConsumption(patient.number, stage.id, stage.name, serviceDueDate, producer));
                }
            }
        }

        creationQueueRef.current = queue.slice(1);
        setBatchTotal(queue.length);
        setBatchCompleted(0);
        props.CreateProtocolConsumption(queue[0]);
    };

    const ResetForm = () => {
        setSubmittedForm(false);
        setIsAssociatedToContractConcept(false);
        setServiceName('');
        setServiceDueDate(undefined);
        setContractConcept(undefined);
        setSelectedStages([]);
        setSelectedPatients([]);
        setCurrencyCode('ARS');
        setQuantity(1);
        setUnitAmount(0);
        setProducer(undefined);
        setStep(1);
        setBatchOverrides([]);
        setBatchTotal(0);
        setBatchCompleted(0);
        creationQueueRef.current = [];
    };

    // ── render ───────────────────────────────────────────────────────────────
    const saveButtonLabel = (() => {
        if (!props.isSaving) return 'Guardar';
        if (batchTotal > 1) return `Guardando ${batchCompleted + 1} de ${batchTotal}...`;
        return 'Guardando...';
    })();
    const handleCancel = () => { props.onClose(); ResetForm(); };

    const primaryAction = step === 1 && isBatch
        ? { label: 'Siguiente', onClick: onGoToStep2, disabled: props.isSaving }
        : { label: saveButtonLabel, onClick: onSubmitForm, loading: props.isSaving };

    return (
        <GuideDrawer
            open={props.isOpen}
            size="medium"
            title={step === 1 ? 'Agregar concepto' : 'Detalles por concepto'}
            subtitle={step === 2 ? `${batchOverrides.length} conceptos` : undefined}
            onDismiss={handleCancel}
            preventDismiss={props.isSaving}
            testId="consumption-form-drawer"
            secondaryAction={{ label: 'Cancelar', onClick: handleCancel }}
            extraActions={step === 2
                ? [{ label: 'Atrás', icon: <ArrowLeft24Regular />, onClick: () => setStep(1), disabled: props.isSaving }]
                : undefined}
            primaryAction={primaryAction}
        >
            {props.failSaving && (
                <ErrorMessageComponent Error={props.errorOnSaving} />
            )}

            {step === 1 && (
                <>
                    {/* ── Información básica ── */}
                    <GuideDrawerSection title="Información básica">
                        <GuideField
                            label="Descripción del concepto"
                            required
                            error={submittedForm && !serviceName.trim() ? 'La descripción es requerida' : undefined}
                        >
                            <Input
                                value={serviceName}
                                onChange={onServiceNameChange}
                                placeholder="Ingrese una descripción"
                            />
                        </GuideField>

                        <DateTimeInput
                            label="Fecha de realización"
                            placeHolder="Seleccione la fecha"
                            selectedDate={serviceDueDate}
                            isSubmitted={false}
                            isRequired={false}
                            errorMessage={undefined}
                            onSelectDate={(date: Date | undefined) => setServiceDueDate(date)}
                        />

                        <GuideField label="Médico realizador">
                            <PractitionerSelectorComponent
                                MultiselectEnable={false}
                                onChangeSelection={(ps) => setProducer(ps?.[0])}
                                SelectedPractitioners={producer ? [producer.userId] : undefined}
                            />
                        </GuideField>
                    </GuideDrawerSection>

                    {/* ── Asignación ── */}
                    <GuideDrawerSection title="Asignación">
                        {props.ContractId && (
                            <ContractStagesSelectorComponent
                                ContractId={props.ContractId}
                                onSelectedContractStage={(stages: ContractStage[]) => setSelectedStages(stages)}
                                ContractStagesSelected={selectedStages.length > 0 ? selectedStages.map(s => s.id) : undefined}
                                MultiselectEnable={true}
                            />
                        )}

                        {props.Protocol?.id && (
                            <ProtocolPatientSelectorComponent
                                ProtocolId={props.Protocol.id}
                                onSelectedProtocolPatient={(patients: ProtocolPatient[]) => setSelectedPatients(patients)}
                                ProtocolPatientsSelectedNumber={selectedPatients.length > 0 ? selectedPatients.map(p => p.numberInProtocol) : undefined}
                                MultiselectEnable={true}
                            />
                        )}
                    </GuideDrawerSection>

                    {/* ── Concepto de contrato ── */}
                    {props.ContractId && (
                        <GuideDrawerSection
                            title="Concepto de contrato"
                            description="Indica si este item está asociado a un concepto del contrato"
                        >
                            <div>
                                <Switch
                                    label="Asociado a un concepto de contrato"
                                    labelPosition="before"
                                    checked={isAssociatedToContractConcept}
                                    onChange={(_, data) => onToggleContractConcept(data.checked)}
                                />
                            </div>

                            {isAssociatedToContractConcept && (
                                <ContractConceptSelectorComponent
                                    ContractId={props.ContractId}
                                    referralDate={serviceDueDate}
                                    onSelectedContractConcept={(concepts: ContractConcept[]) => setContractConcept(concepts?.[0])}
                                    ContractConceptsSelected={contractConcept ? [contractConcept.id] : undefined}
                                    MultiselectEnable={false}
                                />
                            )}
                        </GuideDrawerSection>
                    )}

                    {/* ── Valorización (solo si no tiene concepto de contrato) ── */}
                    {!contractConcept?.id && (
                        <GuideDrawerSection title="Valorización">
                            <CurrencySelectorComponent
                                errorMessage={undefined}
                                defaultSelected={currencyCode}
                                label=""
                                onCurrencyChange={(currency: string) => setCurrencyCode(currency)}
                            />

                            <GuideFieldGrid>
                                <GuideField label="Monto unitario">
                                    <Input
                                        type="number"
                                        min={0}
                                        contentAfter={<span className="consumption-form-currency">{currencyCode}</span>}
                                        value={unitAmount.toString()}
                                        onChange={onUnitAmountChange}
                                    />
                                </GuideField>
                                <GuideField label="Cantidad">
                                    <Input
                                        type="number"
                                        min={1}
                                        contentAfter={<span className="consumption-form-currency">uds.</span>}
                                        value={quantity.toString()}
                                        onChange={onQuantityChange}
                                    />
                                </GuideField>
                            </GuideFieldGrid>

                            <div className="consumption-form-total">
                                <span className="consumption-form-total-label">Total</span>
                                <span className="consumption-form-total-value">
                                    <CurrencyAmountComponent amount={amount} currency={currencyCode} />
                                </span>
                            </div>
                        </GuideDrawerSection>
                    )}

                    {isBatch && (
                        <div className="consumption-form-batch-preview">
                            <Badge appearance="tint" color="informative">
                                Se crearán {effectivePatientsCount * effectiveStagesCount} conceptos
                            </Badge>
                        </div>
                    )}
                </>
            )}

            {step === 2 && batchOverrides.map((item, idx) => (
                <div key={item.key} className="consumption-form-batch-item">
                    <div className="consumption-form-batch-item-header">
                        <Subtitle2>
                            {item.patientNumber || '(sin paciente)'}
                            {item.stageName ? ` — ${item.stageName}` : ''}
                        </Subtitle2>
                        <span className="consumption-form-batch-item-concept">
                            {contractConcept?.concept?.conceptName ?? serviceName}
                        </span>
                    </div>
                    <DateTimeInput
                        label="Fecha de realización"
                        placeHolder="Seleccione la fecha"
                        selectedDate={item.dueDate}
                        isSubmitted={false}
                        isRequired={false}
                        errorMessage={undefined}
                        onSelectDate={(date: Date | undefined) =>
                            updateBatchOverride(idx, { dueDate: date })
                        }
                    />
                    <GuideField label="Médico realizador">
                        <PractitionerSelectorComponent
                            MultiselectEnable={false}
                            onChangeSelection={(ps) =>
                                updateBatchOverride(idx, { producer: ps?.[0] })
                            }
                            SelectedPractitioners={
                                item.producer ? [item.producer.userId] : undefined
                            }
                        />
                    </GuideField>
                </div>
            ))}
        </GuideDrawer>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ConsumptionFormComponent as any);
